import { UPDATE_RFQ_BUYER_NOTIFICATION_USERS } from '@/buyers/hooks/useUpdateRfqBuyerNotificationUsers'
import StoreOrderStepBadge from '@/dealers/components/StoreOrderStepBadge'
import Action from '@/gf/components/Action'
import AddressC from '@/gf/components/Address'
import AutocompleteSelect from '@/gf/components/AutocompleteSelect'
import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Desc from '@/gf/components/Desc'
import UppyFilesManager from '@/gf/components/inputs/UppyFilesManager'
import Layout from '@/gf/components/Layout'
import { InStockPill, LeadTimePill } from '@/gf/components/LineItem'
import LinkGearflow from '@/gf/components/LinkOld'
import MachineDownIcon from '@/gf/components/MachineDownIcon'
import Modal from '@/gf/components/Modal'
import OfficeBuildingTwo from '@/gf/components/OfficeBuildingTwo'
import PointPreview from '@/gf/components/PointPreview'
import RedAlert from '@/gf/components/RedAlert'
import RfqEvents from '@/gf/components/RfqEvents'
import SelectField from '@/gf/components/SelectField'
import SelectUsersModal from '@/gf/components/SelectUsersModal'
import Spinner from '@/gf/components/Spinner'
import { TableWrapper } from '@/gf/components/Table'
import TextArea from '@/gf/components/TextArea'
import TextAreaField from '@/gf/components/TextAreaField'
import Tooltip from '@/gf/components/Tooltip'
import YellowAlert from '@/gf/components/YellowAlert'
import useCompetingOffers from '@/gf/hooks/useCompetingOffers'
import useMsgs from '@/gf/hooks/useMsgs'
import { useTimelineEventsForRfq } from '@/gf/hooks/useTimelineEvents'
import useToggle from '@/gf/hooks/useToggle'
import AttachmentM from '@/gf/modules/Attachment'
import GraphQL from '@/gf/modules/GraphQL'
import LineItemM from '@/gf/modules/LineItem'
import MoneyM from '@/gf/modules/Money'
import Phone from '@/gf/modules/Phone'
import ProductImage from '@/gf/modules/ProductImage'
import StoreOrderEventContext from '@/gf/modules/StoreOrderEvent'
import Time from '@/gf/modules/Time'
import UserM from '@/gf/modules/User'
import SEARCH_SUPPLIERS from '@/gf/queries/users'
import type { RequestForQuoteQuery } from '@/suppliers/_gen/gql'
import {
  RequestForQuoteStep,
  useAddRfqImagesMutation,
  useCopyQuoteQuery,
  useDeleteRfqImageMutation,
  useOpenRfqMutation,
} from '@/suppliers/_gen/gql'
import * as api from '@/suppliers/modules/api'
import RFQCommentForm from '@/suppliers/pages/RequestForQuote/RFQCommentForm'
import { ApolloError, gql, useMutation } from '@apollo/client'
import { XCircleIcon } from '@heroicons/react/24/outline'
import nth from 'lodash/nth'
import pluralize from 'pluralize'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import {
  CreateOrderFormCustomer,
  CustomerCardRef,
  EditLineItem,
  EditLineItemChanges,
  LineItemPayload,
  Money,
  OwnerOption,
  ProductSearchResult,
  RequestForQuote,
  ShippingCost,
  StoreOption,
  SupplierUser,
} from '../../../types'
import AccountMachinesTable from '../../components/AccountMachinesTableV2'
import AddCustomerCard from '../../components/AddCustomerCardForCreateOrder'
import AddItemsCard from '../../components/AddItemsCardForCreateOrder'
import AddressMetadata from '../../components/AddressMetadata'
import CompetingOfferCard from '../../components/CompetingOfferCard'
import CustomerCard from '../../components/CustomerCardForCreateOrder'
import EmailActionModal from '../../components/EmailActionModal'
import NewQuotePayment from '../../components/NewQuotePayment'
import NewStoreOrders from '../../components/NewStoreOrders'
import SupplierAutocomplete from '../../components/SupplierAutocomplete'
import useAdminUsers from '../../hooks/useAdminUsers'
import useCreateQuote from '../../hooks/useCreateQuote'
import useFetchDiscounts from '../../hooks/useFetchDiscounts'
import useGqlClient from '../../hooks/useGqlClient'
import useStores from '../../hooks/useStores'
import useSuppliers from '../../hooks/useSuppliers'
import CreateQuoteStoreForm from '../../modules/CreateQuoteStoreForm'
import StoreOrderM from '../../modules/StoreOrder'
import { CreateQuoteForm, SessionStore, SessionUser } from '../../types'
import PartRequestsAddCard from './PartRequestsAddCard'
import PartRequestsTable from './PartRequestsTable'
import ShippingLocationModalForm from './ShippingLocationModalForm'
import Tvh from './Tvh'
import BillingAddress from '@/gf/components/BillingAddress'

type RfqStore = NonNullable<Rfq['requestForQuoteStores'][number]>
type RfqOwner = Rfq['requestForQuoteOwners'][number]
type Product = Rfq['storeOrders'][number]['lineItems'][number]['product']
type Rfq = Exclude<RequestForQuoteQuery['requestForQuote'], null>

export interface PartRequestPlaceholder {
  id: string
  name: string
  mpn: string
  partNumber: string
  externalId: string | null
  taskNumber: string | null
  suggestion: boolean | null
  quantity: number
  count: number
  addToQuote: boolean
  salePrice?: number
  classification?: number
  description: string
  hidden?: boolean
  inStock?: boolean
  availableAt?: DateTime | null
}

const cancelReasons = {
  locatedElsewhere: 'Located Elsewhere',
  duplicateRequest: 'Duplicate Request',
  noLongerNeeded: 'No Longer Needed',
  other: 'Other',
}

const RequestForQuoteDetails = ({
  rfq,
  refetchRfq,
  user,
  store,
  openMessagesTab,
  copyStoreOrderId,
}: {
  rfq: Rfq
  refetchRfq: (args?: { id: string }) => Promise<unknown>
  user: SessionUser
  store: SessionStore
  openMessagesTab: () => void
  copyStoreOrderId?: string
}) => {
  const gqlClient = useGqlClient()
  const navigate = useNavigate()
  const allStores = useStores().data?.allStores || []
  const allSuppliers = useSuppliers()
  const [partRequests, setPartRequests] = useState<PartRequestPlaceholder[]>([])
  const [copyQuoteItems, setCopyQuoteItems] = useState<PartRequestPlaceholder[]>([])
  const [allOwners, setAllOwners] = useState<SupplierUser[]>([])
  const [estimatedShippingCost, setEstimatedShippingCost] = useState<Money>()
  const [form, setForm] = useState<CreateQuoteForm>({ stores: [] })
  const [_msgs, msgsMgr] = useMsgs()

  const reloadRfq = async () => {
    await refetchRfq({ id: rfq.id })
  }

  const { competingOffers } = useCompetingOffers({ requestForQuoteId: rfq.id })
  const { data: copyQuoteData, error: copyQuoteError } = useCopyQuoteQuery({
    skip: !copyStoreOrderId,
    client: gqlClient,
    variables: { id: copyStoreOrderId ?? '' },
  })

  // Show the default user shipping address if the RFQ doesn't have one
  const rfqShippingAddress = rfq?.shippingAddress ?? rfq?.user?.shippingAddress

  const [cancelReasonText, setCancelReasonText] = useState('')
  const [cancelEmailCheckbox, setCancelEmailCheckbox] = useState<boolean>(true)
  const [addToQuoteVisible, addToQuoteToggler] = useToggle(true)
  const [cancelModalOpen, cancelModalToggler] = useToggle()
  const [openModalOpen, openModalToggler] = useToggle()
  const [closeModalOpen, closeModalToggler] = useToggle()
  const [editShippingAddressModalOpen, editShippingAddressModalToggle] = useToggle()
  const [spinnerLive, spinner] = useToggle()

  const [selectedOwners, setSelectedOwners] = useState<OwnerOption[]>([])
  const [selectedStores, setSelectedStores] = useState<StoreOption[]>([])
  const [shippingCosts, setShippingCosts] = useState<ShippingCost[]>([])
  const [assignModalOpen, assignModalToggler] = useToggle()
  const [assignPerforming, setAssignPerforming] = useState(false)
  const [ownerModalOpen, ownerModalToggler] = useToggle()
  const [deleteOwner, setDeleteOwner] = useState<RfqOwner>()
  const [deleteOwnerOpen, deleteOwnerToggler] = useToggle()
  const [deleteAssigned, setDeleteAssigned] = useState<RfqStore>()
  const [deleteAssignedOpen, deleteAssignedToggler] = useToggle()
  const [updateRfqBuyerNotificationUsers] = useMutation(UPDATE_RFQ_BUYER_NOTIFICATION_USERS)

  const [assignRfqMutation] = useMutation(gql`
    mutation AssignRfq($storeIds: [String], $requestForQuoteId: String) {
      assignRfq(storeIds: $storeIds, requestForQuoteId: $requestForQuoteId)
    }
  `)

  const [ownerRfqMutation] = useMutation(gql`
    mutation AddOwnerRfq($ownerIds: [String], $requestForQuoteId: String) {
      addOwnerRfq(ownerIds: $ownerIds, requestForQuoteId: $requestForQuoteId) {
        id
      }
    }
  `)

  const [cancelRfqMutation] = useMutation(gql`
    mutation CancelRfq(
      $sendEmail: Boolean
      $reason: String
      $cancelRfqReason: String
      $requestForQuoteId: String
    ) {
      cancelRfq(
        sendEmail: $sendEmail
        reason: $reason
        cancelRfqReason: $cancelRfqReason
        requestForQuoteId: $requestForQuoteId
      )
    }
  `)

  const [closeRfqMutation] = useMutation(gql`
    mutation CloseRfq($requestForQuoteId: String) {
      closeRfq(requestForQuoteId: $requestForQuoteId) {
        id
      }
    }
  `)

  const [openRfqMutation] = useOpenRfqMutation({ client: gqlClient })

  const [deleteOwnerRfq] = useMutation(gql`
    mutation DeleteOwnerRfq($ownerId: String, $requestForQuoteId: String) {
      deleteOwnerRfq(ownerId: $ownerId, requestForQuoteId: $requestForQuoteId) {
        id
      }
    }
  `)

  const [deleteAssignedRfq] = useMutation(gql`
    mutation DeleteAssignedRfq($assignedStoreId: String, $requestForQuoteId: String) {
      deleteAssignedRfq(assignedStoreId: $assignedStoreId, requestForQuoteId: $requestForQuoteId)
    }
  `)

  const [deleteImage] = useDeleteRfqImageMutation({ client: gqlClient })
  const timelineEventsResult = useTimelineEventsForRfq(rfq.id)
  const eventsSearch = timelineEventsResult.data?.eventsSearch
  const [deletedImageUrl, setDeletedImageUrl] = useState<string>()

  const { data: adminUsers, loading: adminUsersLoading, error: adminUsersError } = useAdminUsers()

  const [notificationUsersModalOpen, notificationUsersModalToggler] = useToggle()
  const [newOrderLineItems, setNewOrderLineItems] = useState<EditLineItem[]>([])
  const [productsFromQuery, setProductsFromQuery] = useState<ProductSearchResult[]>([])
  const [additions, setAdditions] = useState<EditLineItemChanges>({})
  const [orderCustomer, setOrderCustomer] = useState<CreateOrderFormCustomer>()

  const orgId = rfq.user?.organization?.id || orderCustomer?.account?.id

  const discounts = (() => {
    const result = useFetchDiscounts().data?.discounts

    return orgId ? result?.filter((d) => d.organization.id === orgId) : result
  })()

  const customerCardRef = useRef<CustomerCardRef>(null)
  const [createModalOpen, createModalToggler] = useToggle()
  const createQuoteMutation = useCreateQuote()
  const [notesText, setNotesText] = useState<string>('')
  const [addressNotDeliverableModalOpen, addressNotDeliverableModalToggler] = useToggle(false)

  const [selectedReasonId, setSelectedReasonId] = useState<
    'locatedElsewhere' | 'duplicateRequest' | 'noLongerNeeded' | 'other'
  >('locatedElsewhere')
  const [additionalDetails, setAddtionalDetails] = useState<string>('')
  const openShippingAddress = () => {
    customerCardRef.current?.focusShippingAddress()
  }

  const [addRfqImages] = useAddRfqImagesMutation({ client: gqlClient })

  useEffect(() => {
    // Handle original part requests from the RFQ
    if (rfq.partRequests.length > 0) {
      const placeholders = rfq.partRequests.map((pr) => {
        return {
          id: pr.id,
          mpn: pr.mpn,
          name: pr.description,
          partNumber: pr.mpn,
          externalId: pr.externalId,
          taskNumber: pr.taskNumber,
          suggestion: pr.suggestion,
          description: pr.description,
          quantity: pr.rfqQuantity ?? pr.quantity,
          count: pr.rfqQuantity ?? pr.quantity,
          addToQuote: true,
          hidden: false,
          inStock: undefined,
          availableAt: null,
        }
      })
      setPartRequests(placeholders)
    } else {
      setPartRequests([])
    }
  }, [rfq.partRequests])

  useEffect(() => {
    // Only use items from copyQuoteData if available
    if (copyQuoteData?.storeOrder?.lineItems && copyQuoteData.storeOrder.lineItems.length > 0) {
      const copiedItems = copyQuoteData.storeOrder.lineItems.map((item) => ({
        id: item.id,
        mpn: item.rfqPart?.mpn || item.product?.mpn || '',
        name: item.rfqPart?.description || item.product?.name || '',
        partNumber: item.rfqPart?.mpn || item.product?.mpn || '',
        externalId: null,
        taskNumber: null,
        suggestion: null,
        description: item.rfqPart?.description || item.product?.name || '',
        quantity: item.quantity,
        count: item.quantity,
        addToQuote: true,
        hidden: false,
        inStock: item.inStock ?? undefined,
        availableAt: item.availableAt ? DateTime.fromISO(item.availableAt) : null,
        salePrice: item.unitPrice ? MoneyM.toDecimal(item.unitPrice) : undefined,
      }))

      setCopyQuoteItems(copiedItems)
    } else {
      setCopyQuoteItems([])
    }
  }, [copyQuoteData])

  useEffect(() => {
    if (copyQuoteData?.storeOrder && rfq) {
      setShippingCosts([
        ...shippingCosts,
        {
          // Use the user's Store instead of the copyStoreOrder Store, because the user is creating this Quote
          storeId: store.id,
          shippingCost: MoneyM.toDecimal(copyQuoteData.storeOrder.shippingAmount).toString(),
        },
      ])
    }
  }, [copyQuoteData, rfq])

  useEffect(() => {
    const addedLineItems = Object.keys(additions)
      .filter((key) => !additions[key].id)
      .map((key) => additions[key])

    api
      .calculateShippingCost(
        store.id,
        addedLineItems.map((ali) => ({
          productId: ali.product.id,
          quantity: ali.quantity,
          inStock: ali.inStock,
          availableAt: ali.availableAt,
          unitPrice: MoneyM.toDecimal(ali.unitPrice),
        }))
      )
      .then(setEstimatedShippingCost)

    setNewOrderLineItems(addedLineItems)
  }, [additions])

  const rfqOwnerNames = rfq.requestForQuoteOwners.map((rfqOwner) => rfqOwner.user.displayName)

  const assignOwner = async () => {
    try {
      if (selectedOwners.length === 0) {
        msgsMgr.add('No owner selected.', 'negative')
        return
      }

      const ownerIds = selectedOwners.map((selection) => selection.value)
      await ownerRfqMutation({
        variables: { ownerIds, requestForQuoteId: rfq.id },
      })

      await reloadRfq()

      ownerModalToggler.off()

      msgsMgr.add('RFQ owner added.', 'positive')
    } catch (error) {
      const err = error as { graphQLErrors: { message: string }[] }
      spinner.off()
      const message = err.graphQLErrors.reduce((acc, { message: msg }) => `${acc}${msg}`, '')
      msgsMgr.add(message, 'negative')
    }
  }

  const supplierOptions = allSuppliers.map((u) => ({
    value: u.id,
    label: `${u.name} ${u.email}`,
  }))

  const storeOptions = allStores.map((sto) => ({
    value: sto.id,
    label: `${sto.name}${sto?.alias ? ` (${sto.alias})` : ''}`,
  }))

  const storeIds = selectedStores.map((selection) => selection.value)

  const assignSupplier = () => {
    if (selectedStores.length === 0) {
      msgsMgr.add('No supplier selected.', 'negative')
      return
    }

    setAssignPerforming(true)
    assignRfqMutation({ variables: { storeIds, requestForQuoteId: rfq.id } })
      .then(() => {
        msgsMgr.add('RFQ assigned.', 'positive')
        navigate('/rfqs')
      })
      .catch(() => {
        msgsMgr.add('Error', 'negative')
        return refetchRfq()
      })
      .finally(() => {
        setAssignPerforming(false)
        assignModalToggler.off()
      })
  }

  const rfqQuotes = rfq.storeOrders || []

  const cancelRfqAction = async () => {
    spinner.on()

    await cancelRfqMutation({
      variables: {
        cancelRfqReason:
          selectedReasonId === 'other' ? additionalDetails : cancelReasons[selectedReasonId],
        reason: cancelReasonText,
        sendEmail: cancelEmailCheckbox,
        requestForQuoteId: rfq.id,
      },
    })

    cancelModalToggler.off()

    msgsMgr.add('RFQ closed.', 'positive')

    navigate('/rfqs')
  }

  const openRfqAction = () => {
    spinner.on()

    openRfqMutation({
      variables: {
        requestForQuoteId: rfq.id,
      },
    })
      .then(() => {
        msgsMgr.add('RFQ reopened.', 'positive')
        return reloadRfq()
      })
      .catch(() => {
        msgsMgr.add('Failed to reopen RFQ', 'negative')
      })
      .finally(() => {
        openModalToggler.off()
        spinner.off()
      })
  }

  const closeRfqAction = () => {
    spinner.on()

    closeRfqMutation({
      variables: {
        requestForQuoteId: rfq.id,
      },
    })
      .then(() => {
        closeModalToggler.off()

        msgsMgr.add('RFQ closed.', 'positive')

        navigate('/rfqs')
      })
      .catch(() => {
        msgsMgr.add('Failed to mark RFQ closed', 'negative')
      })
  }

  const deleteOwnerFromRfq = async (e) => {
    try {
      e.preventDefault()

      if (!deleteOwner) {
        msgsMgr.add('Error removing Owner. No Owner selected.', 'negative')
        deleteOwnerToggler.off()
        return
      }

      spinner.on()

      await deleteOwnerRfq({
        variables: { requestForQuoteId: rfq.id, ownerId: deleteOwner.id },
      })

      await reloadRfq()

      setDeleteOwner(undefined)
      spinner.off()
      deleteOwnerToggler.off()

      msgsMgr.add('Owner removed.', 'positive')
    } catch (error) {
      const err = error as { graphQLErrors: { message: string }[] }
      spinner.off()

      const message = err.graphQLErrors.reduce((acc, { message: msg }) => `${acc}${msg}`, '')
      msgsMgr.add(message, 'negative')
    }
  }

  const currentRfqStore = store && rfq.requestForQuoteStores.find((a) => a.storeId === store.id)

  const deleteAssignedFromRfq = async (e) => {
    e.preventDefault()
    if (!deleteAssigned) return

    spinner.on()

    const promise = deleteAssignedRfq({
      variables: { requestForQuoteId: rfq.id, assignedStoreId: deleteAssigned.id },
    })
      .then(() => {
        msgsMgr.add('Assignment deleted.', 'positive')
      })
      .catch(() => {
        msgsMgr.add('Error deleting assignment', 'negative')
      })

    if (store.id === deleteAssigned.storeId) {
      navigate('/rfqs')
    } else {
      promise.finally(spinner.off)
      await reloadRfq()
      setDeleteAssigned(undefined)
      deleteAssignedToggler.off()
    }
  }

  const removeAssigned = (assigned: RfqStore) => {
    setDeleteAssigned(assigned)
    deleteAssignedToggler.on()
  }

  const deleteSupplierText = UserM.isAdmin(user) ? (
    'Delete Assigned Supplier'
  ) : (
    <>Can&apos;t Participate</>
  )

  const undoImageDeletion = () => {
    if (deletedImageUrl) {
      addRfqImages({ variables: { id: rfq.id, imageUrls: [deletedImageUrl] } }).then(() => {
        reloadRfq()
        setDeletedImageUrl(undefined)
      })
    }
  }

  const cannotParticipate = () => {
    if (currentRfqStore) removeAssigned(currentRfqStore)
  }

  const canDisplayCustomerContactInfo = () => {
    if (UserM.isAdmin(user)) return true
    if (rfq.step === RequestForQuoteStep.Inbound) return false

    return (
      rfq.storeOrders.filter((so) =>
        StoreOrderM.canDisplayCustomerContactInfo(
          so as unknown as RequestForQuote['storeOrders'][number],
          user
        )
      ).length > 0
    )
  }

  const displayCustomerContactInfo = canDisplayCustomerContactInfo()

  const newStoreOrders = StoreOrderM.editLineItemsToNewStoreOrders(
    newOrderLineItems,
    allStores,
    shippingCosts,
    discounts
  )

  const createQuote = (allowUnverifiedAddress = false) => {
    if (!rfq.enabled) {
      createModalToggler.off()
      msgsMgr.add('This RFQ is currently closed.', 'negative')
      return
    }

    if (newStoreOrders.length === 0) {
      createModalToggler.off()
      msgsMgr.add('A quote must contain items.', 'negative')
      return
    }

    // Validate email
    const customerEmail = orderCustomer?.email || rfq.emailAddress
    if (!customerEmail) {
      createModalToggler.off()
      msgsMgr.add('Customer email is required to create a quote.', 'negative')
      return
    }

    // Validate phone
    const customerPhone = orderCustomer?.phoneNumber || rfq.phoneNumber
    if (!customerPhone) {
      createModalToggler.off()
      msgsMgr.add('Customer phone number is required to create a quote.', 'negative')
      return
    }

    spinner.on()

    // probably don't need to filter; just being cautious
    const streIds = newStoreOrders
      .filter((nso) => nso.lineItems.length !== 0)
      .map((nso) => nso.store.id)

    // Validate pickup address only for stores that have line items and pickup enabled
    const pickupStores = form.stores.filter((s) => s.pickup && streIds.includes(s.id))
    const missingPickupAddress = pickupStores.find(
      (s) => !s.pickupAddress?.lineOne || !s.pickupAddress?.city || !s.pickupAddress?.state
    )
    if (missingPickupAddress) {
      spinner.off()
      createModalToggler.off()
      msgsMgr.add('Pickup address is required for stores with pickup enabled.', 'negative')
      return
    }

    const stores = form.stores
      .filter((s) => streIds.includes(s.id))
      // if order is being shipped, clear out pickup address
      .map((s) => (s.pickup ? s : { ...s, pickupAddress: null }))

    const lineItems = newStoreOrders
      .flatMap((nso) => nso.lineItems)
      .map((li) => ({
        productId: li.product.id,
        quantity: li.quantity,
        inStock: li.inStock,
        availableAt: li.availableAt,
        externalId: li.externalId,
        taskNumber: li.taskNumber,
        suggestion: li.suggestion,
        unitPrice: MoneyM.toDecimal(li.unitPrice),
      }))

    const timingDetails = stores
      .map((s) => ({ storeId: s.id, timingDetails: s.timingDetails }))
      .filter((tdi) => tdi.timingDetails.length > 0)

    createQuoteMutation({
      stores: stores.map((s) => ({
        id: s.id,
        pickupAddress: s.pickupAddress ? GraphQL.cleanGraphQlInput(s.pickupAddress) : null,
      })),
      notes: notesText,
      timingDetails,
      pickupInputs: stores.map((s) => ({ storeId: s.id, pickup: s.pickup })),
      payOnAccount: false,
      payOnInvoice: false,
      lineItems,
      shippingCosts,
      allowUnverifiedAddress,
      requestForQuoteId: rfq.id,
      customer: StoreOrderM.orderFormCustomerToCustomerInput(orderCustomer),
      customerShim: null,
    })
      .then(() => {
        msgsMgr.add('Quote created. Email sent to customer.', 'positive')
        navigate('/rfqs')
      })
      .catch((err: ApolloError) => {
        const { message } = err

        spinner.off()
        createModalToggler.off()

        if (message === 'Address not deliverable') {
          Promise.resolve().then(() => {
            addressNotDeliverableModalToggler.on()
          })
        } else {
          Promise.resolve().then(() => {
            addressNotDeliverableModalToggler.off()
          })

          msgsMgr.add(message, 'negative')
        }
      })
  }

  const activePartRequests = partRequests.filter((pr) => pr.addToQuote)
  const shownPartRequests = partRequests.filter((pr) => !pr.hidden)

  const addProduct = (product: ProductSearchResult) => {
    const existingLineItem = newOrderLineItems.find((li) => li.product.id === product.id)
    const currencyCode = existingLineItem?.unitPrice.currency.code || 'USD'
    const quantity = existingLineItem ? existingLineItem.quantity + 1 : 1
    const discount = discounts?.find((d) => d.store.id === product.store.id)

    const discountedUnitPrice = MoneyM.fromDecimal(
      product.salePrice * (1 - (discount?.percent || 0) / 100),
      currencyCode
    )

    const newAdditions = {
      ...additions,
      [product.id]: {
        id: undefined,
        product,
        quantity,
        showMpn: true,
        externalId: null,
        taskNumber: null,
        suggestion: null,
        inStock: true,
        availableAt: null,
        unitPrice: MoneyM.fromDecimal(product.salePrice, currencyCode),
        discountedUnitPrice,
        extendedPrice: MoneyM.mult(discountedUnitPrice, quantity),
      },
    }

    setAdditions(newAdditions)

    if (!form.stores.find((s) => s.id === product.store.id)) {
      const pickupAddress = (user.role === 'admin' && store.address) || null
      const storeForm = CreateQuoteStoreForm.build({ id: product.store.id, pickupAddress })
      setForm({ ...form, stores: [...form.stores, storeForm] })
    }

    // Update the search results to show the correct items in the order
    const existingLineItems = Object.keys(newAdditions)
      .filter((key) => !newAdditions[key].id)
      .map((key) => newAdditions[key])

    const idsInOrder = existingLineItems.map((li) => li.product.id)

    const productSearchResults = productsFromQuery.map((p) => ({
      ...p,
      inOrder: idsInOrder?.includes(p.id) || false,
    }))

    setProductsFromQuery(productSearchResults)
  }

  const url = (image: Product['images'][number] | undefined, variantName: string) => {
    const variants = image ? image.variants : ProductImage.defaultVariants
    const variant = variants.filter((v) => v.name === variantName)[0]
    return variant.url
  }

  const primaryImageUrl = (product: Product, variantName: string) =>
    url(nth(product.images, 0), variantName)

  const onTvhPartAdded = (lineItems: LineItemPayload[]) => {
    const allAdditions = lineItems.reduce(
      (acc, lineItem) => ({
        ...acc,
        [lineItem.product.id]: LineItemM.fromPayload(lineItem),
      }),
      additions
    )

    setAdditions(allAdditions)
  }

  const rfqStores = rfq.requestForQuoteStores.map((rfqs) => ({
    ...rfqs,
    nonparticipation: rfq.nonparticipations.find((n) => n.storeId === rfqs.storeId),
  }))

  return (
    <Layout>
      <Layout.Section type="primary">
        {competingOffers.map((competingOffer) => (
          <CompetingOfferCard key={competingOffer.id} storeOrder={competingOffer} rfq={rfq} />
        ))}
        <Card
          title="RFQ Details"
          headerButton={
            currentRfqStore && (
              <Action.S onClick={() => cannotParticipate()}>Can&apos;t Participate</Action.S>
            )
          }
        >
          <Card.Section>
            <Card.SubSection className="text-sm text-gray-700">
              <Desc.List>
                {rfq.neededBy && (
                  <Desc.Row title="Needed By" className="bg-amber-100 text-amber-900">
                    {Time.toString(rfq.neededBy)}
                  </Desc.Row>
                )}
                <Desc.Row title="Received">{Time.toString(rfq.insertedAt)}</Desc.Row>
                {displayCustomerContactInfo && <Desc.Row title="Name">{rfq.fullName}</Desc.Row>}
                {rfq.zipCode && <Desc.Row title="Zip Code">{rfq.zipCode}</Desc.Row>}
                {displayCustomerContactInfo && (
                  <Desc.Row title="Phone">{Phone.format(rfq.phoneNumber)}</Desc.Row>
                )}
                {UserM.isAdmin(user) && rfq.emailAddress && (
                  <Desc.Row title="Email">{rfq.emailAddress}</Desc.Row>
                )}
                {shownPartRequests.length > 0 && (
                  <Desc.Row title="Request">
                    <TableWrapper>
                      <PartRequestsTable
                        addToQuoteVisible={addToQuoteVisible}
                        partRequests={shownPartRequests}
                        setPartRequests={setPartRequests}
                      />
                    </TableWrapper>
                  </Desc.Row>
                )}
                {rfq.partsRequest && (
                  <Desc.Row title="Additional details">
                    <div className="whitespace-pre-wrap">{rfq.partsRequest}</div>
                  </Desc.Row>
                )}
                {rfq.machineInformation && (
                  <Desc.Row title=" Machine Information">{rfq.machineInformation}</Desc.Row>
                )}
                {rfq.orgMachines.length !== 0 && (
                  <Desc.Row
                    title={
                      <div className="flex flex-row gap-x-2 justify-start items-center">
                        {pluralize('Machines', rfq.orgMachines.length)}
                        {rfq.machineDown && <MachineDownIcon />}
                      </div>
                    }
                  >
                    <TableWrapper>
                      <AccountMachinesTable accountMachines={rfq.orgMachines} />
                    </TableWrapper>
                    <YellowAlert
                      className="mt-2"
                      title={`Please verify the ${pluralize(
                        'parts',
                        partRequests.length
                      )} requested ${partRequests.length === 1 ? 'fits' : 'fit'} this machine`}
                    />
                  </Desc.Row>
                )}
                <Desc.Row title="Shipping Preference">{rfq.shippingOption}</Desc.Row>
              </Desc.List>
            </Card.SubSection>
          </Card.Section>
        </Card>

        <>
          {addToQuoteVisible &&
            ((copyQuoteItems.length > 0 &&
              // Show copied quote items if available
              (copyStoreOrderId && !copyQuoteData && !copyQuoteError ? (
                <Spinner />
              ) : (
                <>
                  {copyQuoteError && <RedAlert title="Error copying Quote" />}
                  <PartRequestsAddCard
                    organizationId={orgId}
                    allStores={allStores}
                    setPartRequests={setCopyQuoteItems}
                    partRequests={copyQuoteItems}
                    onComplete={addToQuoteToggler.off}
                    additions={additions}
                    setAdditions={setAdditions}
                    initialStore={
                      // Use the user's Store instead of the StoreOrder Store, because the user is creating this Quote
                      copyQuoteData?.storeOrder ? { value: store.id, label: store.name } : undefined
                    }
                  />
                </>
              ))) ||
              // Fallback to original behavior if no copied items
              (copyQuoteItems.length === 0 && activePartRequests.length > 0 && (
                <PartRequestsAddCard
                  organizationId={orgId}
                  allStores={allStores}
                  setPartRequests={setPartRequests}
                  partRequests={activePartRequests}
                  onComplete={addToQuoteToggler.off}
                  additions={additions}
                  setAdditions={setAdditions}
                  initialStore={undefined}
                />
              )))}
          <AddItemsCard
            allStores={allStores}
            productsFromQuery={productsFromQuery}
            setProductsFromQuery={setProductsFromQuery}
            newOrderLineItems={newOrderLineItems}
            addProduct={addProduct}
          />
          {user.role === 'admin' && <Tvh orgId={orgId} onPartAdded={onTvhPartAdded} />}
          <NewStoreOrders
            newStoreOrders={newStoreOrders}
            shippingCosts={shippingCosts}
            setShippingCosts={setShippingCosts}
            setAdditions={setAdditions}
            additions={additions}
            productsFromQuery={productsFromQuery}
            setProductsFromQuery={setProductsFromQuery}
            form={form}
            setForm={setForm}
          />
          <NewQuotePayment
            newStoreOrders={newStoreOrders}
            shippingCosts={shippingCosts}
            suggestedShippingCost={estimatedShippingCost}
            form={form}
            secondaryFooterActions={
              UserM.isAdmin(user)
                ? undefined
                : [
                    {
                      id: 1,
                      element: (
                        <Button primary={false} onClick={cannotParticipate}>
                          Can&apos;t Participate
                        </Button>
                      ),
                    },
                  ]
            }
            primaryFooterAction={{
              id: 1,
              title: 'Place Quote',
              callBack: () => createModalToggler.on(),
            }}
          />
        </>

        <Card title="Timeline">
          <Card.Section>
            <Card.SubSection>
              <RFQCommentForm
                rfq={rfq}
                refetch={timelineEventsResult.refetch}
                isAdmin={UserM.isAdmin(user)}
                openMessagesTab={openMessagesTab}
              />
            </Card.SubSection>
            <Card.SubSection>
              <RfqEvents
                events={eventsSearch?.rfqEvents}
                storeOrderEvents={eventsSearch?.storeOrderEvents}
                storeOrderEventMapper={StoreOrderEventContext.getSupplierEventDisplayData(user)}
                showStoreNames={UserM.isAdmin(user)}
              />
            </Card.SubSection>
          </Card.Section>
        </Card>
      </Layout.Section>
      <Layout.Section type="secondary">
        <>
          {rfqQuotes.map((storeOrder) => (
            <Card
              key={storeOrder.id}
              title={
                <div className="flex flex-row space-x-2">
                  <p>{`Order ${storeOrder.shortId}`}</p>
                  <StoreOrderStepBadge step={storeOrder.step} />
                  {UserM.isAdmin(user) && (
                    <p className="pl-4 flex text-xs text-gray-500 items-center">
                      {storeOrder.store.name}
                    </p>
                  )}
                </div>
              }
              headerButton={
                <Link
                  to={`/orders/${storeOrder.id}`}
                  className="text-white border-transparent bg-gearflow hover:bg-yellow-700 focus:ring-yellow-500 inline-flex items-center px-4 py-2 border text-sm rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                  View Order
                </Link>
              }
            >
              <Card.Section>
                {storeOrder.lineItems.map((item) => (
                  <Card.SubSection key={item.id}>
                    <div className="flex space-x-4 text-sm">
                      <img
                        src={primaryImageUrl(item.product, 'card')}
                        alt={item.product.name}
                        className="w-16 h-16 border rounded-sm overflow-hidden"
                      />
                      <div className="flex flex-1 flex-col">
                        <LinkGearflow to={`/products/${item.product.id}`}>
                          {item.product.name}
                        </LinkGearflow>
                        <p className="text-gray-500">{`MPN: ${item.product.mpn}`}</p>
                        {item.inStock ? (
                          <InStockPill className="self-start" />
                        ) : item.inStock !== null && item.availableAt ? (
                          <div className="flex flex-row justify-start items-center space-x-1 sm:space-x-1.5">
                            <LeadTimePill availableAt={item.availableAt} />
                            <Tooltip
                              text="This is the estimated date shared with the customer to indicate when the part will be ready"
                              size="medium"
                            />
                          </div>
                        ) : null}
                        {UserM.isAdmin(user) && item.product.altMpn && (
                          <p className="text-xs text-gray-500">{`Alt MPN: ${item.product.altMpn}`}</p>
                        )}
                      </div>
                      <p>
                        {MoneyM.format(item.discountedUnitPrice)} x {item.quantity}
                      </p>
                      <p>{MoneyM.format(item.extendedPrice)}</p>
                    </div>
                  </Card.SubSection>
                ))}
              </Card.Section>
              {storeOrder.timingDetails && (
                <Card.Section title="Expected shipping/lead time">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {storeOrder.timingDetails}
                  </p>
                </Card.Section>
              )}
            </Card>
          ))}
          <Card
            title="Owner"
            primaryFooterAction={
              rfqOwnerNames.length === 0 || UserM.isAdmin(user)
                ? {
                    id: 1,
                    element: (
                      <Button primary={false} title="Add Owner" onClick={ownerModalToggler.on} />
                    ),
                  }
                : undefined
            }
          >
            <Card.Section>
              {rfq.requestForQuoteOwners.map((rfqOwner) => (
                <div key={rfqOwner.userId} className="flex w-full items-center justify-between">
                  <p>{rfqOwner.user.displayName}</p>
                  <XCircleIcon
                    className="h-6 w-6 cursor-pointer text-gray-400 hover:text-red-400"
                    onClick={() => {
                      setDeleteOwner(rfqOwner)
                      deleteOwnerToggler.on()
                    }}
                  />
                </div>
              ))}
              {rfqOwnerNames.length === 0 && (
                <p className="text-gray-700 pt-2">No owner selected</p>
              )}
            </Card.Section>
          </Card>
          {allStores && allStores.length > 0 && (
            <Card
              title="Assignments"
              primaryFooterAction={{
                id: 1,
                title: 'Assign Supplier',
                callBack: assignModalToggler.on,
              }}
            >
              <Card.Section>
                {rfqStores.map((rfqStore) => (
                  <div key={rfqStore.id} className="flex w-full items-center gap-x-1">
                    {UserM.isAdmin(user) && rfqStore.store.createdByBuyer && (
                      <OfficeBuildingTwo className="inline-block h-4 text-blue-500" />
                    )}

                    <p className={`flex-grow ${rfqStore.storeCanceledAt ? 'line-through' : ''}`}>
                      {rfqStore.nonparticipation ? (
                        <Tooltip
                          text={`Reason: ${
                            rfqStore.nonparticipation.reason === 'Other'
                              ? rfqStore.nonparticipation.details
                              : rfqStore.nonparticipation.reason
                          }`}
                        >
                          {rfqStore.store.name}
                        </Tooltip>
                      ) : (
                        rfqStore.store.name
                      )}
                    </p>

                    <XCircleIcon
                      className="h-6 w-6 cursor-pointer text-gray-400 hover:text-red-400"
                      onClick={() => removeAssigned(rfqStore)}
                    />
                  </div>
                ))}

                {rfq.requestForQuoteStores.length === 0 && (
                  <p className="text-gray-700">No suppliers assigned</p>
                )}
              </Card.Section>
            </Card>
          )}
          {/* Show shipping address in its own card if user doesn't exist */}
          {!rfq.user && rfq.shippingAddress && (
            <Card title="Shipping address">
              <Card.Section>
                {rfq.shippingAddress ? (
                  <>
                    <AddressC
                      hideName={!displayCustomerContactInfo}
                      address={rfq.shippingAddress}
                      className="text-sm text-gray-900"
                    />
                    {typeof rfq.shippingAddress.deliverable === 'boolean' && (
                      <AddressMetadata className="mt-2" address={rfq.shippingAddress} />
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No shipping address</p>
                )}
              </Card.Section>
            </Card>
          )}
          {rfq.user && (
            <Card title="Customer">
              {displayCustomerContactInfo && (
                <Card.Section>
                  <Link
                    className="text-sm text-blue-500 underline"
                    to={`/customers/${rfq.user.id}`}
                  >
                    {rfq.user.displayName}
                  </Link>
                </Card.Section>
              )}

              {displayCustomerContactInfo && (
                <Card.Section title="Contact Information">
                  <p className="text-sm text-gray-700">{rfq.user.phoneNumber}</p>
                  {UserM.isAdmin(user) && <p className="text-sm text-gray-700">{rfq.user.email}</p>}
                </Card.Section>
              )}

              <Card.Section
                title="Shipping"
                headerButton={
                  UserM.isAdmin(user) ? (
                    <Action.T
                      className="text-sm"
                      onClick={() => editShippingAddressModalToggle.on()}
                    >
                      Edit
                    </Action.T>
                  ) : undefined
                }
              >
                {rfqShippingAddress ? (
                  <>
                    {rfqShippingAddress.point && (
                      <PointPreview point={rfqShippingAddress.point} className="text-sm mb-1" />
                    )}

                    <AddressC
                      hideName={!displayCustomerContactInfo}
                      address={rfqShippingAddress}
                      className="text-sm text-gray-700"
                    />

                    {typeof rfqShippingAddress.deliverable === 'boolean' && (
                      <AddressMetadata className="mt-2" address={rfqShippingAddress} />
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No shipping address</p>
                )}
              </Card.Section>

              <Card.Section title="Billing">
                {rfq.billingAddress ? (
                  <BillingAddress
                    className="text-sm text-gray-700"
                    companyName={rfq.company.name}
                    address={rfq.billingAddress}
                  />
                ) : (
                  <p className="text-sm text-gray-500">No billing address</p>
                )}
              </Card.Section>
            </Card>
          )}
          {!rfq.user && orderCustomer && (
            <CustomerCard
              ref={customerCardRef}
              orderCustomer={orderCustomer}
              setOrderCustomer={setOrderCustomer}
            />
          )}
          {!rfq.user && !orderCustomer && <AddCustomerCard setOrderCustomer={setOrderCustomer} />}
          {UserM.isAdmin(user) && (
            <Card
              title="Notifications (Buyer)"
              headerButton={
                <Action.T className="text-sm" onClick={notificationUsersModalToggler.on}>
                  Edit
                </Action.T>
              }
            >
              <Card.Section>
                {rfq.buyerNotificationUsers.length === 0 ? (
                  <p className="text-sm text-gray-700">None.</p>
                ) : (
                  rfq.buyerNotificationUsers.map(({ id, name }) => (
                    <p key={id} className="text-sm text-gray-700">
                      {name}
                    </p>
                  ))
                )}
              </Card.Section>
            </Card>
          )}
          {UserM.isAdmin(user) && !rfq.enabled && (
            <Card title="Status">
              <Card.Section>
                <div className="flex flex-wrap gap-2 pb-6">
                  <p>This RFQ is closed but you can reopen it</p>
                </div>
                <div className="flex justify-between">
                  <Button primary={false} onClick={openModalToggler.on}>
                    Reopen
                  </Button>
                </div>
              </Card.Section>
            </Card>
          )}

          {UserM.isAdmin(user) && rfq.enabled && (
            <Card title="Status">
              <Card.Section>
                <div className="flex flex-wrap gap-2 pb-6">
                  <p>You can mark the RFQ as closed, cancelled or fulfilled</p>
                </div>
                <div className="flex justify-between">
                  <Button primary={false} onClick={closeModalToggler.on}>
                    Close
                  </Button>
                  <Button primary={false} onClick={cancelModalToggler.on}>
                    Cancel
                  </Button>
                </div>
              </Card.Section>
            </Card>
          )}

          {rfq.images && (
            <Card title="Images">
              <Card.Section>
                {deletedImageUrl && (
                  <div className="mb-4 bg-yellow-50 text-base px-4 py-1 rounded-md">
                    Image deleted. <Action.T onClick={undoImageDeletion}>Undo</Action.T>
                  </div>
                )}
                <UppyFilesManager
                  fileUrls={rfq.images.map((image) => image.url)}
                  uploadContext="supplier_rfq_images"
                  onAdd={(addFileUrls) =>
                    addRfqImages({ variables: { id: rfq?.id, imageUrls: addFileUrls } }).then(
                      reloadRfq
                    )
                  }
                  onRemove={(removeFileUrl) =>
                    deleteImage({ variables: { rfqId: rfq.id, imageUrl: removeFileUrl } })
                      .then(() => reloadRfq())
                      .finally(() => setDeletedImageUrl(removeFileUrl))
                  }
                  onError={(error) => msgsMgr.add(error, 'negative')}
                  allowedFileTypes={AttachmentM.defaultAllowedFileTypes}
                />
              </Card.Section>
            </Card>
          )}
        </>
      </Layout.Section>
      <EmailActionModal
        title="Cancel RFQ"
        open={cancelModalOpen}
        onClose={cancelModalToggler.off}
        onSubmit={cancelRfqAction}
        submitButtonText="Submit"
        isWaiting={spinnerLive}
        emailText={cancelReasonText}
        setEmailText={setCancelReasonText}
        sendEmailCheckbox={cancelEmailCheckbox}
        setSendEmailCheckbox={setCancelEmailCheckbox}
      >
        <p className="prose">This will deny all open quotes.</p>

        <SelectField
          label="Reason"
          options={Object.keys(cancelReasons).map((key) => ({
            id: key,
            display: cancelReasons[key],
          }))}
          currentId={selectedReasonId}
          onChange={(id: string) => {
            if (
              id === 'locatedElsewhere' ||
              id === 'duplicateRequest' ||
              id === 'noLongerNeeded' ||
              id === 'other'
            ) {
              setSelectedReasonId(id)
            }
          }}
        />

        {selectedReasonId === 'other' && (
          <>
            <p className="text-gray-700">Please provide a reason for cancelling this request.</p>
            <TextAreaField
              label="Additional Details"
              value={additionalDetails}
              onChange={(e) => {
                setAddtionalDetails(e?.target.value)
              }}
            />
          </>
        )}
      </EmailActionModal>
      <Modal
        open={closeModalOpen}
        onClose={closeModalToggler.off}
        title="Close RFQ"
        footerAction={<Button title="Submit" onClick={closeRfqAction} />}
      >
        <div>
          {rfq && rfq.id && (
            <p className="mb-2">{`Are you sure you want to mark RFQ: ${rfq.shortId} as closed?`}</p>
          )}
        </div>
      </Modal>
      <Modal
        open={openModalOpen}
        onClose={openModalToggler.off}
        title="Reopen RFQ"
        footerAction={<Button title="Submit" onClick={openRfqAction} />}
      >
        <div>
          {rfq && rfq.id && (
            <p className="mb-2">{`Are you sure you want to reopen RFQ: ${rfq.shortId}?`}</p>
          )}
        </div>
      </Modal>
      {UserM.isAdmin(user) && (
        <SelectUsersModal
          titleText="Update Notification Recipients"
          detailsText="Select the people who receive notifications for this RFQ."
          open={notificationUsersModalOpen}
          onClose={notificationUsersModalToggler.off}
          onSubmit={(userIds) => {
            spinner.on()
            updateRfqBuyerNotificationUsers({
              variables: { rfqId: rfq.id, userIds },
            })
              .then(() => reloadRfq())
              .catch(() => {
                msgsMgr.add('Whoops, something went wrong. Please contact support.', 'negative')
              })
              .finally(() => {
                notificationUsersModalToggler.off()
                spinner.off()
              })
          }}
          spinnerLive={spinnerLive}
          users={adminUsers}
          usersLoading={adminUsersLoading}
          usersError={!!adminUsersError}
          initialSelectedUsers={rfq.buyerNotificationUsers}
        />
      )}

      <Modal
        open={deleteAssignedOpen}
        onClose={deleteAssignedToggler.off}
        title={deleteSupplierText}
        footerAction={<Button title="Submit" onClick={deleteAssignedFromRfq} />}
      >
        <div>
          {deleteAssigned && deleteAssigned.store && UserM.isAdmin(user) && (
            <p className="mb-2">
              Do you want to delete assigned supplier {deleteAssigned.store.name} from RFQ?
            </p>
          )}
          {deleteAssigned && deleteAssigned.store && !UserM.isAdmin(user) && (
            <p className="mb-2">Do you wish to remove yourself from this RFQ?</p>
          )}
        </div>
      </Modal>

      <Modal
        open={deleteOwnerOpen}
        onClose={deleteOwnerToggler.off}
        title="Delete Owner"
        footerAction={<Button title="Submit" onClick={deleteOwnerFromRfq} />}
      >
        <div>
          {deleteOwner && deleteOwner.user && (
            <p className="mb-2">
              Do you want to delete owner {deleteOwner.user.displayName} from RFQ?
            </p>
          )}
        </div>
      </Modal>
      <Modal
        open={ownerModalOpen}
        onClose={ownerModalToggler.off}
        title="Add Owner"
        footerAction={<Button title="Submit" onClick={() => assignOwner()} />}
      >
        <div>
          <SupplierAutocomplete
            label="User"
            defaultOptions={supplierOptions}
            allData={allOwners}
            query={SEARCH_SUPPLIERS}
            selected={selectedOwners}
            reactSelectOptions={{ isMulti: false }}
            onChange={(option) => {
              setSelectedOwners([option])
            }}
            transformation={(response) => {
              const supplierOwners = response.data.searchSuppliers.map((u) => ({
                value: u.id,
                label: `${u.name} ${u.email}`,
              }))

              setAllOwners(supplierOwners)

              return supplierOwners
            }}
          />
        </div>
      </Modal>
      <Modal
        open={assignModalOpen}
        onClose={assignModalToggler.off}
        title="Assign to supplier"
        footerAction={
          <Button title="Submit" onClick={() => assignSupplier()} performing={assignPerforming} />
        }
      >
        <div>
          <AutocompleteSelect
            label="Store"
            name="storeSelection"
            className="w-full text-sm mb-1"
            value={selectedStores}
            options={storeOptions}
            onChange={setSelectedStores}
            reactSelectOptions={{ isSearchable: true, isMulti: true }}
          />
        </div>
      </Modal>
      {/* There was a bug where scrolling would lock when quickly switching between on/off createModalOpen and
              addressNotDeliverableModalOpen modals. Explicitely removing the modals when their state variables are false
              fixes this bug. */}
      {createModalOpen && (
        <Modal
          open={createModalOpen}
          onClose={createModalToggler.off}
          title="Create Quote"
          footerAction={
            <Button title="Submit" onClick={() => createQuote()} disabled={spinnerLive} />
          }
        >
          {spinnerLive ? (
            <Spinner />
          ) : (
            <>
              <p>Creating a new quote will notify the customer.</p>

              <p className="mt-4">Add additional comments for customer (optional)</p>
              <p>ATTENTION: The customer will see these comments.</p>
              <TextArea
                className="mt-2"
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
              />
            </>
          )}
        </Modal>
      )}
      {addressNotDeliverableModalOpen && (
        <Modal
          open={addressNotDeliverableModalOpen}
          onClose={() => {
            addressNotDeliverableModalToggler.off()
            openShippingAddress()
          }}
          title="Warning"
          cancelText="Change address"
          withIcon
          footerAction={
            <Button
              title="Continue without changing"
              onClick={() => createQuote(true)}
              disabled={spinnerLive}
            />
          }
        >
          {spinnerLive ? (
            <Spinner />
          ) : (
            <>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  We were not able to verify the shipping address as a deliverable address:
                </p>
                {orderCustomer && (
                  <AddressC
                    className="mt-2 text-sm text-gray-700"
                    address={orderCustomer?.shippingAddress}
                  />
                )}
              </div>
            </>
          )}
        </Modal>
      )}

      <ShippingLocationModalForm
        rfqId={rfq.id}
        organizationId={rfq.user?.organization?.id || null}
        open={editShippingAddressModalOpen}
        onClose={editShippingAddressModalToggle.off}
        onAfterSave={() => {
          refetchRfq()
          editShippingAddressModalToggle.off()
        }}
      />
    </Layout>
  )
}

export default RequestForQuoteDetails
